import React, { createRef, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { useCloudIdToUrl } from '@atlaskit/linking-common/hooks';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { universalTypeFilterId } from '../../../../common/constants/filters';
import { ProductKeys } from '../../../../common/constants/products';
import { AIModeTabs } from '../../../../common/ui/ai-mode';
import { useOverflowObserver } from '../../../../common/ui/overflow-observer';
import { useWidthObserverRegistration } from '../../../../common/ui/width-observer';
import {
	useCloudId,
	useFiltersDiscoverability2Enabled,
	usePrimaryProduct,
	useSearchQuery,
	useSelectedProduct,
} from '../../../../controllers/store';
import {
	useAnyFilterSelected,
	useFiltersToDisplay,
	useSearchActions,
} from '../../../../controllers/store/search';
import { filter as archived } from '../../../../controllers/store/search/filters/archived';
import { filter as archivedAndPendingComponents } from '../../../../controllers/store/search/filters/archived-and-pending-components';
import { filter as assetsSchema } from '../../../../controllers/store/search/filters/assets-schema';
import { filter as assetsType } from '../../../../controllers/store/search/filters/assets-type';
import { filter as assignee } from '../../../../controllers/store/search/filters/assignee';
import { filter as content } from '../../../../controllers/store/search/filters/content';
import { filter as contributor } from '../../../../controllers/store/search/filters/contributor';
import { filter as createdBy } from '../../../../controllers/store/search/filters/created-by';
import { filter as focusAreaType } from '../../../../controllers/store/search/filters/focus-area-type';
import { filter as focusAreaUnder } from '../../../../controllers/store/search/filters/focus-area-under';
import { filter as label } from '../../../../controllers/store/search/filters/label';
import { filter as lastUpdated } from '../../../../controllers/store/search/filters/last-updated';
import { filter as lastUpdatedBy } from '../../../../controllers/store/search/filters/last-updated-by';
import { filter as owner } from '../../../../controllers/store/search/filters/owner';
import { filter as pageStatus } from '../../../../controllers/store/search/filters/page-status';
import { filter as project } from '../../../../controllers/store/search/filters/project';
import { filter as reporter } from '../../../../controllers/store/search/filters/reporter';
import { filter as space } from '../../../../controllers/store/search/filters/space';
import { filter as status } from '../../../../controllers/store/search/filters/status';
import { filter as titleMatchOnly } from '../../../../controllers/store/search/filters/title-match-only';
import { filter as verified } from '../../../../controllers/store/search/filters/verified';

import AppSelectFilter from './app-select';
import ArchivedFilter from './archived';
import ArchivedAndPendingComponentsFilter from './archived-and-pending-components';
import AssetsSchemaFilter from './assets-schema';
import AssetsTypeFilter from './assets-type';
import AssigneeFilter from './assignee';
import ContentFilter from './content';
import ContributorFilter from './contributor';
import CreatedByFilter from './created-by';
import DropdownFilter from './dropdown';
import FocusAreaTypeFilter from './focus-area-type';
import FocusAreaUnderFilter from './focus-area-under';
import LabelFilter from './label';
import LastUpdatedFilter from './last-updated';
import LastUpdatedByFilter from './last-updated-by';
import { i18n } from './messages';
import OwnerFilter from './owner';
import PageStatusFilter from './page-status';
import ProjectFilter from './project';
import ReporterFilter from './reporter';
import { ResultsSort } from './results-sort';
import SpaceFilter from './space';
import StatusFilter from './status';
import TitleMatchOnlyFilter from './title-match-only';
import UniversalTypeFilter from './universal-type';
import VerifiedFilter from './verified';

export const filters = {
	[archived.id]: ArchivedFilter,
	[lastUpdated.id]: LastUpdatedFilter,
	[universalTypeFilterId]: UniversalTypeFilter,
	[contributor.id]: ContributorFilter,
	[space.id]: SpaceFilter,
	[focusAreaType.id]: FocusAreaTypeFilter,
	[focusAreaUnder.id]: FocusAreaUnderFilter,
	[verified.id]: VerifiedFilter,
	[assignee.id]: AssigneeFilter,
	[reporter.id]: ReporterFilter,
	[project.id]: ProjectFilter,
	[content.id]: ContentFilter,
	[titleMatchOnly.id]: TitleMatchOnlyFilter,
	[owner.id]: OwnerFilter,
	[createdBy.id]: CreatedByFilter,
	[lastUpdatedBy.id]: LastUpdatedByFilter,
	[label.id]: LabelFilter,
	[pageStatus.id]: PageStatusFilter,
	[status.id]: StatusFilter,
	[archivedAndPendingComponents.id]: ArchivedAndPendingComponentsFilter,
	[assetsSchema.id]: AssetsSchemaFilter,
	[assetsType.id]: AssetsTypeFilter,
};

const inlineStyles = xcss({
	overflow: 'hidden',
	padding: 'space.050',
	margin: 'space.negative.050',
	flex: '1 0 auto',
	position: 'absolute',
	zIndex: 'card',
});

const aiTabsStyles = xcss({
	position: 'absolute',
	zIndex: 'card',
	padding: 'space.050',
	margin: 'space.negative.050',
});

const inlineWithAIModeTabsStyles = xcss({
	overflow: 'hidden',
	padding: 'space.050',
	margin: 'space.negative.050',
	flex: '1 0 auto',
	position: 'absolute',
	zIndex: 'card',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	marginInlineStart: '190px' as any,
});

const hiddenReferenceInlineStyles = xcss({
	overflow: 'hidden',
	padding: 'space.050',
	margin: 'space.negative.050',
	flex: '1 0 auto',
	visibility: 'hidden',
	position: 'relative',
	zIndex: '1',
});

const filterRowStyles = xcss({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
});

const suggestedFiltersDividerStyles = xcss({
	borderLeft: `${token('border.width')} solid ${token('color.border')}`,
	height: '29px',
	marginInline: 'space.150',
	width: '1px',
});

const suggestedFilterStyles = xcss({
	alignItems: 'center',
	color: 'color.text.subtlest',
	display: 'flex',
	gap: 'space.100',
	width: 'max-content',
});

interface ClearAllButtonProps {
	/** Called when the user wants to clear all filters. */
	onClear: VoidFunction;
}

export const ClearAllButton = ({ onClear }: ClearAllButtonProps) => {
	const { formatMessage } = useIntl();
	return (
		<Box id="clear-all-button">
			<Button onClick={onClear} appearance="subtle">
				<Text>{formatMessage(i18n.clearFilters)}</Text>
			</Button>
		</Box>
	);
};

const SuggestedFilters = ({ primaryProduct }: { primaryProduct: ProductKeys }) => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={suggestedFilterStyles}>
			<Box xcss={suggestedFiltersDividerStyles}></Box>
			<Text as="p">{`${formatMessage(i18n.suggestedFilters)}:`}</Text>
			{primaryProduct === ProductKeys.Confluence && <SpaceFilter showPrimaryProductIcon />}
			{primaryProduct === ProductKeys.Assets && fg('assets_rovo_search_all') && (
				<AssetsSchemaFilter showPrimaryProductIcon />
			)}
			{primaryProduct === ProductKeys.Jira && (
				<>
					<ProjectFilter showPrimaryProductIcon />
					{
						// Remove the Assignee filter when cleaning up rovo_fps_nin_cta
						!fg('rovo_fps_nin_cta') && <AssigneeFilter showPrimaryProductIcon />
					}
				</>
			)}
		</Box>
	);
};

type ProductFiltersProps = {
	containerRef: React.MutableRefObject<HTMLDivElement | undefined>;
};

const ProductFilters = ({ containerRef }: ProductFiltersProps) => {
	const [cloudId] = useCloudId();
	const [query] = useSearchQuery();
	const { data: tenantDomain } = useCloudIdToUrl(cloudId || '');
	const [{ filtersInLine }] = useFiltersToDisplay();
	const [productSelected] = useSelectedProduct();
	const { resetSort, clearAllFilters } = useSearchActions();
	const [isAnyFilterSelected] = useAnyFilterSelected();
	const isConfluence = productSelected === ProductKeys.Confluence;
	const isCompass = productSelected === ProductKeys.Compass;
	const [primaryProduct] = usePrimaryProduct();
	const filtersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	// Elements in the navbar that will never be hidden despite resizes
	const alwaysVisibleRef = useRef<HTMLDivElement>();
	const dropdownRef = useRef<HTMLDivElement>();
	const appSelectFilterRef = useRef<HTMLDivElement>();

	let filtersRendered = filtersInLine.filter(
		(id) => id !== universalTypeFilterId || productSelected !== ProductKeys.Atlas,
	);

	// Hide content under filter from navbar QS-5384
	if (isConfluence) {
		filtersRendered = filtersRendered.filter((id) => id !== content.id);
	} else if (isCompass) {
		filtersRendered = filtersRendered.filter((id) => id !== archivedAndPendingComponents.id);
	} else if (productSelected === ProductKeys.Assets && fg('assets_rovo_search_all')) {
		filtersRendered = filtersRendered.filter((id) => id !== contributor.id);
	}

	if (
		// don't show assets filters on townsquare if the flag is set
		// eslint-disable-next-line @atlaskit/platform/no-preconditioning
		primaryProduct === ProductKeys.Atlas &&
		fg('assets_rovo_search_all') &&
		!fg('rovo_search_show_assets_filters_in_townsquare')
	) {
		filtersRendered = filtersRendered.filter(
			(id) => id !== assetsSchema.id && id !== assetsType.id,
		);
	}

	const filterRefs = useRef<React.MutableRefObject<HTMLDivElement>[]>([]);

	filterRefs.current = filtersRendered
		.filter((id) => filters[id as keyof typeof filters])
		.map((_, i) => filterRefs.current[i] ?? createRef());

	const visibleFilters = useOverflowObserver({
		containerRef,
		alwaysVisibleRef,
		itemRefs: filterRefs,
		itemIds: filtersRendered,
	});

	const filtersRef = useRef(null);
	useWidthObserverRegistration('filters', filtersRef);

	const displaySuggestedConfluenceFilters =
		primaryProduct === ProductKeys.Confluence && !isAnyFilterSelected;
	const displaySuggestedAssetsFilters =
		primaryProduct === ProductKeys.Assets && !isAnyFilterSelected && fg('assets_rovo_search_all');
	const displaySuggestedJiraFilters =
		primaryProduct === ProductKeys.Jira && !isAnyFilterSelected && fg('jira_fps_suggested_filter');
	const displaySuggestedFilters =
		displaySuggestedConfluenceFilters ||
		displaySuggestedAssetsFilters ||
		displaySuggestedJiraFilters;

	return (
		<Box xcss={filterRowStyles}>
			<Inline space="space.100" xcss={hiddenReferenceInlineStyles}>
				<Box
					style={{
						width: dropdownRef.current?.offsetWidth ?? 40,
					}}
				/>
				{filtersDiscoverability2Enabled && (
					<Box
						style={{
							width: appSelectFilterRef.current?.offsetWidth ?? 96,
						}}
					/>
				)}
				{filtersRendered.map((id, index) => {
					const Filter = filters[id as keyof typeof filters];
					if (!Filter) {
						return null;
					}
					const elementId = `reference:${id}`;
					return (
						<Box key={elementId} id={elementId} ref={filterRefs.current[index]}>
							{id === lastUpdated.id || id === universalTypeFilterId ? (
								<Filter testId={`ref-test-id-${id}`} />
							) : (
								<Filter />
							)}
						</Box>
					);
				})}
			</Inline>
			{fg('rovo_ai_mode_restore_toggle_search') && (
				<Box xcss={aiTabsStyles}>
					<AIModeTabs query={query} tenantDomain={tenantDomain} />
				</Box>
			)}
			<Inline
				space="space.100"
				xcss={fg('rovo_ai_mode_restore_toggle_search') ? inlineWithAIModeTabsStyles : inlineStyles}
				ref={filtersRef}
			>
				<Inline ref={dropdownRef}>
					<DropdownFilter />
				</Inline>
				{filtersDiscoverability2Enabled && (
					<Inline ref={appSelectFilterRef}>
						<AppSelectFilter />
					</Inline>
				)}
				{filtersRendered.map((id) => {
					const Filter = filters[id as keyof typeof filters];
					if (!visibleFilters.includes(id) || !Filter) {
						return null;
					}
					return (
						<Box key={id} id={id}>
							<Filter />
						</Box>
					);
				})}
				<Inline space="space.100" ref={alwaysVisibleRef}>
					{isConfluence && <ResultsSort />}
					{isAnyFilterSelected ? (
						<ClearAllButton
							onClear={() => {
								clearAllFilters();
								resetSort();
							}}
						/>
					) : null}
					{displaySuggestedFilters && <SuggestedFilters primaryProduct={primaryProduct} />}
				</Inline>
			</Inline>
		</Box>
	);
};

export default ProductFilters;
